/**
 * Pre-auth workspace branding — PURE CORE (no imports, no DOM, no env).
 *
 * Kept dependency-free for the same reason as lib/firebaseStorageConfig:
 * the modules that own the build-time brand (lib/branding) and the DOM
 * applier read `import.meta.env` at module scope, which the jest runtime
 * cannot load — so the merge logic lives here and is unit-testable, and
 * lib/preAuthBranding.ts is the thin browser glue around it.
 *
 * The merge contract (WHITELABEL_ANALYSIS G1 + the fallback guardrail):
 * the workspace's auth-config branding wins PER FIELD, and only when the
 * field is a non-empty string; every missing / empty / mistyped field
 * falls back to the build-time default for that field. A null config (fetch
 * failed, unknown host → 404, service down → 503) resolves to the defaults
 * untouched, so pre-auth surfaces render EXACTLY today's branding.
 */

/** Structural mirror of BrandConfig (lib/branding) — declared here so this
 *  module needs no imports; the two are structurally identical and the
 *  glue passes the real brand singleton where this shape is expected. */
export interface BrandColorsShape {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  accent: string;
  textLight?: string;
}

export interface BrandConfigShape {
  businessName: string;
  tagline: string;
  supportEmail: string;
  colors: BrandColorsShape;
  logoUrl: string;
  faviconUrl: string;
  links: {
    termsUrl: string;
    privacyUrl: string;
    copyright: string;
  };
  features: {
    jobBoard: boolean;
    events: boolean;
    sponsorPortal: boolean;
  };
  roleSetup: {
    sponsor: boolean;
    employer: boolean;
    referrer: boolean;
  };
}

/** The subset of the GET /api/workspace/auth-config response the pre-auth
 *  branding consumes. The server sends `branding` verbatim from the
 *  workspace doc, so every branding field is validated at runtime
 *  (non-empty string) rather than trusted. */
export interface AuthConfigBrandingPayload {
  workspaceId?: string | null;
  name?: unknown;
  branding?: Record<string, unknown> | null;
}

/** Effective brand for pre-auth surfaces: build-time defaults with any
 *  usable workspace fields merged over them. */
export interface PreAuthBrand {
  businessName: string;
  tagline: string;
  supportEmail: string;
  colors: BrandColorsShape;
  /** Generic logo (workspace branding.logoUrl, else the build-time logo). */
  logoUrl: string;
  /** Login-specific logo (workspace branding.loginLogoUrl only; '' when the
   *  workspace sets none — callers fall back to logoUrl, as AuthPage always
   *  has). */
  loginLogoUrl: string;
  /** Workspace favicon (branding.faviconUrl, else branding.logoUrl), else
   *  the build-time favicon ('' → the static index.html icon stays). */
  faviconUrl: string;
  copyright: string;
  /** True when an auth-config payload resolved for this host (even one
   *  carrying no usable branding fields). */
  workspaceResolved: boolean;
  workspaceId: string | null;
}

/** The server's own last-resort display name when a workspace doc has
 *  neither name nor orgName — NOT a brand identity, so it never overrides
 *  the build-time businessName. */
const SERVER_NAME_SENTINEL = 'ISATS Portal';

function usableString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function firstUsable(...values: unknown[]): string {
  for (const value of values) {
    const s = usableString(value);
    if (s) return s;
  }
  return '';
}

/**
 * Pure merge: auth-config payload → effective pre-auth brand.
 */
export function resolvePreAuthBrand(
  config: AuthConfigBrandingPayload | null | undefined,
  defaults: BrandConfigShape,
): PreAuthBrand {
  const raw: Record<string, unknown> =
    config && typeof config.branding === 'object' && config.branding !== null
      ? config.branding
      : {};
  const configName = usableString(config?.name);
  const textLight = firstUsable(raw.textLight, defaults.colors.textLight);

  return {
    businessName: firstUsable(
      raw.businessName,
      configName && configName !== SERVER_NAME_SENTINEL ? configName : '',
      defaults.businessName,
    ),
    tagline: firstUsable(raw.tagline, defaults.tagline),
    supportEmail: firstUsable(raw.supportEmail, defaults.supportEmail),
    colors: {
      primary: firstUsable(raw.primary, defaults.colors.primary),
      primaryLight: firstUsable(raw.primaryLight, defaults.colors.primaryLight),
      primaryDark: firstUsable(raw.primaryDark, defaults.colors.primaryDark),
      accent: firstUsable(raw.accent, defaults.colors.accent),
      ...(textLight ? { textLight } : {}),
    },
    logoUrl: firstUsable(raw.logoUrl, defaults.logoUrl),
    loginLogoUrl: firstUsable(raw.loginLogoUrl),
    faviconUrl: firstUsable(raw.faviconUrl, raw.logoUrl, defaults.faviconUrl),
    copyright: firstUsable(raw.copyright, defaults.links.copyright),
    workspaceResolved: !!config,
    workspaceId:
      typeof config?.workspaceId === 'string' && config.workspaceId
        ? config.workspaceId
        : null,
  };
}

/** Rebuild a full brand config from an effective pre-auth brand so DOM
 *  application goes through the one existing applyBrandToDOM() path.
 *  Surfaces the workspace branding object does not carry (legal URLs,
 *  feature toggles, role setup) always come from the build-time defaults. */
export function toBrandConfig(
  effective: PreAuthBrand,
  defaults: BrandConfigShape,
): BrandConfigShape {
  return {
    businessName: effective.businessName,
    tagline: effective.tagline,
    supportEmail: effective.supportEmail,
    colors: { ...effective.colors },
    logoUrl: effective.logoUrl,
    faviconUrl: effective.faviconUrl,
    links: {
      termsUrl: defaults.links.termsUrl,
      privacyUrl: defaults.links.privacyUrl,
      copyright: effective.copyright,
    },
    features: defaults.features,
    roleSetup: defaults.roleSetup,
  };
}
